import React, { useState, useEffect, useMemo, createContext, useContext, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { useEmployees as useEmployeesQuery } from '@/hooks/useQueryHooks';
import { User, UserRole } from '@/types';
import { UserRole as UserRoleEnum } from '@/types/enums';

/* eslint-disable react-refresh/only-export-components */

// Define enums for better type safety and consistency
export enum EmployeeStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  SUSPENDED = 'Suspended',
  TERMINATED = 'Terminated'
}

export enum ResidencyType {
  RESIDENT = 'Resident',
  NON_RESIDENT = 'Non-Resident',
  EXPATRIATE = 'Expatriate'
}

export enum WorkerType {
  FULL_TIME = 'Full-time',
  PART_TIME = 'Part-time',
  CONTRACTOR = 'Contractor',
  INTERN = 'Intern',
  TEMPORARY = 'Temporary'
}

// Define SortDirection type alias if not already globally available
type SortDirection = 'asc' | 'desc';

interface EmployeesContextType {
  employees: User[];
  isLoading: boolean;
  error: string | null;
  refreshEmployees: () => void;
  getEmployeeById: (id: string) => User | undefined;
  getEmployeesByDepartment: (department: string) => User[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  setPage: (page: number) => void;
  // Add sort state and updater
  sortField: string;
  sortDirection: SortDirection;
  setSort: (field: string, direction: SortDirection) => void;
}

export const EmployeesContext = createContext<EmployeesContextType | undefined>(undefined);

export function EmployeesProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoadingAuth: authLoading } = useAuth();
  const [pageSize] = useState(10); // Standard page size for pagination
  const [currentPage, setCurrentPage] = useState(0); // 0-based page indexing
  const [sortField, setSortField] = useState<string>('name'); // Default sort field
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc'); // Default sort direction
  
  // Only fetch when auth is done loading AND user has appropriate role
  const shouldFetch = !authLoading && !!user && [UserRoleEnum.PLANT_MANAGER, UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN, UserRoleEnum.DEPARTMENT_HEAD, UserRoleEnum.SUPERINTENDENT].includes(user.role as UserRoleEnum);
  
  // Remove pagination for analytics/lookup use cases
  const shouldFetchAll = true; // Always fetch all employees for analytics tables
  
  const {
    data, 
    isLoading: employeesLoading, 
    error: queryError,
    refetch
  } = useEmployeesQuery({
    page: shouldFetchAll ? 0 : currentPage,
    pageSize: shouldFetchAll ? 10000 : pageSize, // Large page size to fetch all
    sortField,
    sortDirection,
    department: (user?.role === UserRoleEnum.DEPARTMENT_HEAD || user?.role === UserRoleEnum.SUPERINTENDENT) ? user.department : undefined, // Filter by department for dept heads and superintendents
    enabled: shouldFetch,
  });
  
  // Combine auth loading and employees loading
  const isLoading = authLoading || employeesLoading;
  
  // Memoize the employees array to prevent unnecessary re-renders
  const employees = useMemo(() => {
    if (!data?.employees) return [];
    
    // Process employees data directly
    return data.employees.map((emp: any) => ({
      id: emp.id,
      email: emp.email || '',
      role: (emp.role as UserRole) || UserRoleEnum.EMPLOYEE,
      name: emp.name || '',
      department: typeof emp.department === 'string' ? emp.department : '',
      position: emp.position || '',
      companyId: emp.companyId || '',
      shift: emp.shift || '',
      status: emp.status || EmployeeStatus.ACTIVE,
      residency: emp.residency || ResidencyType.RESIDENT,
      workerType: emp.workerType || WorkerType.FULL_TIME,
      employee_id: emp.employee_id, // Include employee_id from the database
    }));
  }, [data]);

  const totalCount = useMemo(() => data?.totalCount || 0, [data]);
  const totalPages = useMemo(() => data?.totalPages || 0, [data]);
  const error = queryError ? (queryError as Error).message : null;
  
  useEffect(() => {
    if (employees.length > 0) {
      // employees loaded
    }
  }, [employees.length, sortField, sortDirection]);
  
  // Memoize these functions to prevent unnecessary re-renders
  const refreshEmployees = useCallback(() => {
    refetch();
  }, [refetch]);

  const getEmployeeById = useCallback((id: string) => {
    return employees.find(emp => emp.id === id);
  }, [employees]);

  const getEmployeesByDepartment = useCallback((department: string) => {
    return employees.filter(emp => emp.department === department);
  }, [employees]);

  const setSort = useCallback((field: string, direction: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(0); // Reset to first page when sorting
    // Data will refetch due to queryKey change in useEmployeesQuery
  }, []);

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const contextValue = useMemo(() => ({
    employees,
    isLoading,
    error,
    refreshEmployees,
    getEmployeeById,
    getEmployeesByDepartment,
    totalCount,
    currentPage,
    totalPages,
    setPage,
    sortField,
    sortDirection,
    setSort,
  }), [
    employees,
    isLoading,
    error,
    refreshEmployees,
    getEmployeeById,
    getEmployeesByDepartment,
    totalCount,
    currentPage,
    totalPages,
    setPage,
    sortField,
    sortDirection,
    setSort
  ]);

  return (
    <EmployeesContext.Provider value={contextValue}>
      {children}
    </EmployeesContext.Provider>
  );
}

export function useEmployees() {
  const context = useContext(EmployeesContext);
  if (context === undefined) {
    throw new Error('useEmployees must be used within an EmployeesProvider');
  }
  return context;
}
